export interface ITimeRange {
  start: moment.Moment;
  end: moment.Moment;
}

export interface ITimeRangeFormatted {
  start: string;
  end: string;
}

type unixTimeStamp = number;

export class TimeUtilityService {
  todayStart: unixTimeStamp;

  constructor() {
    this.todayStart = moment().startOf('day').unix();
  }

  getDateRange(currentDate: moment.Moment, timeWindow: any): ITimeRange {
    return {
      start: moment(currentDate).startOf(timeWindow.key),
      end: moment(currentDate).endOf(timeWindow.key)
    };
  }

  diffDateRange(diff: unixTimeStamp, timeWindow: any, format: string): ITimeRange | ITimeRangeFormatted {
    const now = moment().add(diff, timeWindow.key);

    if (format) {
      return this.formattedDateRange(now, timeWindow, format);
    }

    return this.getDateRange(now, timeWindow);
  }

  findQuarter(date: moment.Moment): ITimeRange {
    const startOfYear = moment(date).startOf('year');
    let start = moment(startOfYear);
    let end = moment(start).add(3, 'months').subtract(1, 'seconds');
    let quarter = {
      start,
      end
    };
    // Initial quarter start/end dates...

    for (let i = 0; i < 4; i++) {
      if (moment(date).isBetween(start, end)) {
        quarter = {
          start,
          end
        };
        break;
      }

      start = moment(start).add(3, 'months');
      end = moment(end).add(3, 'months').endOf('month');
    }

    return quarter;
  }

  formatDateRange(range: ITimeRange, format: string): ITimeRangeFormatted {
    return {
      start: range.start.format(format),
      end: range.end.format(format)
    };
  }

  formattedDateRange(currentDate: moment.Moment, timeWindow: ITimeRange, format: string): ITimeRangeFormatted {
    const range = this.getDateRange(currentDate, timeWindow);

    return {
      start: range.start.format(format),
      end: range.end.format(format)
    };
  }

  today9AM(unixTime: unixTimeStamp): unixTimeStamp {
    return moment.unix(unixTime).startOf('day').add(540, 'minute').unix();
  }

  dayDiff(unixTime: unixTimeStamp): unixTimeStamp {
    return moment.unix(unixTime).startOf('day').diff(moment.unix(this.todayStart), 'days');
  }

  isOverdue(unixTime: unixTimeStamp): boolean {
    return unixTime < moment().unix();
  }

  isToday(unixTime: unixTimeStamp): boolean {
    return this.dayDiff(unixTime) === 0;
  }

  isTomorrow(unixTime: unixTimeStamp): boolean {
    return this.dayDiff(unixTime) === 1;
  }

  isThisWeek(unixTime: unixTimeStamp): boolean {
    return moment.unix(unixTime).week() === moment().week() && this.isSameWeekYear(unixTime);
  }

  isNextWeek(unixTime: unixTimeStamp): boolean {
    return moment.unix(unixTime).week() === moment().week() + 1 && this.isSameWeekYear(unixTime);
  }

  isSameWeekYear(unixTime: unixTimeStamp): boolean {
    return moment.unix(unixTime).weekYear() === moment().weekYear();
  }

  firstDayOfTheWeek(unixTime: unixTimeStamp): unixTimeStamp {
    return moment.unix(unixTime).startOf('week').unix();
  }

  lastDayOfTheWeek(unixTime: unixTimeStamp): unixTimeStamp {
    return moment.unix(unixTime).endOf('week').unix();
  }

  formattedFirstDayOfTheWeek(unixTime: unixTimeStamp): string {
    return moment.unix(unixTime).startOf('week').format('MMM DD');
  }

  formattedLastDayOfTheWeek(unixTime: unixTimeStamp): string {
    return moment.unix(unixTime).endOf('week').format('MMM DD');
  }

  formattedWeek(unixTime: unixTimeStamp): string {
    const regularWeekString =
      this.formattedFirstDayOfTheWeek(unixTime) + ' - ' + this.formattedLastDayOfTheWeek(unixTime);
    return this.isSameWeekYear(unixTime)
      ? regularWeekString
      : regularWeekString + ' ' + moment.unix(unixTime).weekYear();
  }

  formattedMonthDay(unixTime: unixTimeStamp): string {
    return moment.unix(unixTime).format('ddd, M/D');
  }

  formattedTime(unixTime: unixTimeStamp): string {
    return moment.unix(unixTime).format('h:mm a');
  }

  // UTC formats
  formattedUtcShortMonthDay(utcTime: unixTimeStamp): string {
    return moment.utc(utcTime).format('MMM D');
  }

  formattedUtcShortWeekdayShortMonthDay(utcTime: unixTimeStamp): string {
    return moment.utc(utcTime).format('ddd, MMM D');
  }
}

export const TimeUtility = new TimeUtilityService();



// WEBPACK FOOTER //
// ./app/vendor/utility-js-time/src/lib/time_utility.ts